import { useQuery } from '@tanstack/react-query';
import { AxiosError, AxiosResponseHeaders } from 'axios';
import { API } from 'network/api';
import { Farmer } from 'types/common';

const fetchFarmers = async (phoneNumber: string, signal: AbortSignal) => {
  try {
    const res = await API.get<Farmer[]>(
      `farmers/shared-farmer-info?phoneNumber=${phoneNumber}`,
      { signal },
    );

    if (!res?.data)
      throw new Error(
        'Error occured while trying to fetch farmers information. Please try again',
      );

    if (res?.data?.length === 0) throw new Error('No data available');

    return {
      data: res?.data,
      headers: res?.headers,
    };
  } catch (err) {
    if (err instanceof Error || err instanceof AxiosError) throw err;
    throw new Error('Something went wrong');
  }
};

const useSharedPhoneNumberQuery = (phoneNumber?: string, farmerId?: string) => {
  const {
    data: response,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['shared-phonenumber', { phoneNumber }],
    queryFn: ({ signal }) => fetchFarmers(phoneNumber ?? '', signal),
    select: (res) => ({
      ...res,
      data: res?.data?.filter((farmer) => farmer?.id !== farmerId),
    }),
    enabled: !!phoneNumber,
    staleTime: 1000 * 10, // 10 seconds
    retry: (failureCount, error) => {
      if (error instanceof AxiosError && error.response?.status === 400) {
        return false;
      }
      return failureCount < 3;
    },
  });

  const data = response?.data;
  const headers = response?.headers as AxiosResponseHeaders;
  const total = Number(headers?.get('X-Total-Count')) || 0;

  return {
    isLoading,
    isError,
    data,
    error,
    total,
    fetch: refetch,
  };
};

export default useSharedPhoneNumberQuery;
